import { ReactNode } from 'react';

import { makeStyles } from '@material-ui/core/styles';

import {
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiMore2Fill,
} from '@remixicon/react';

import { ButtonIcon, Menu, MenuItem, MenuTrigger, Text } from '@backstage/ui';

import { DateUtils } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';

import { ArtifactIcon } from '../ArtifactIcon';
import { ArtifactListItem } from '../ArtifactListItem';

const useStyles = makeStyles({
  primaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

interface ExternalUrlArtifactListItemProps {
  artifact: AttachmentFragment;
  icon: ReactNode;
  subtitleLabel: string;
  openLabel: string;
  fallbackTitle?: (artifact: AttachmentFragment) => string;
  onDelete?: () => void;
}

const defaultFallbackTitle = (artifact: AttachmentFragment) => artifact.uri;

export const ExternalUrlArtifactListItem = ({
  artifact,
  icon,
  subtitleLabel,
  openLabel,
  fallbackTitle = defaultFallbackTitle,
  onDelete,
}: ExternalUrlArtifactListItemProps) => {
  const classes = useStyles();
  const dateLabel = DateUtils.toDateString(
    new Date(artifact.createTime),
    DateUtils.MONTH_DAY_FORMAT,
  );

  const open = () => {
    window.open(artifact.uri, '_blank', 'noopener,noreferrer');
  };

  return (
    <ArtifactListItem.Root onClick={open}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body-medium" as="div" className={classes.primaryText}>
            {artifact.displayName || fallbackTitle(artifact)}
          </Text>
        }
        secondary={
          <Text
            variant="body-x-small"
            as="div"
            color="secondary"
            className={classes.secondaryText}
          >
            {subtitleLabel} &middot; {dateLabel}
          </Text>
        }
      />
      <ArtifactListItem.Action>
        <MenuTrigger>
          <ButtonIcon
            variant="tertiary"
            size="small"
            icon={<RiMore2Fill size={16} />}
            aria-label="Artifact actions"
          />
          <Menu placement="bottom end">
            <MenuItem iconStart={<RiExternalLinkLine />} onClick={open}>
              {openLabel}
            </MenuItem>
            {onDelete && (
              <MenuItem
                color="danger"
                iconStart={<RiDeleteBinLine />}
                onClick={onDelete}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        </MenuTrigger>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};
